import { QRCodeSVG } from "qrcode.react";
import { motion, AnimatePresence } from "framer-motion";
import { Users } from "lucide-react";
import { tr } from "@/lib/i18n";

export const LobbyScene = ({ game, players, lang, onStart, onKick }) => {
  const joinUrl = `${window.location.origin}/join?pin=${game.pin}`;
  return (
    <>
      <div className="store-bg" style={{ filter: "brightness(0.3) blur(3px)", transform: "scale(1.04)" }} />
      <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, rgba(6,8,16,0.82) 0%, rgba(6,8,16,0.32) 42%, rgba(6,8,16,0.74) 100%)" }} />

      <div className="absolute left-[3em] top-[3em] w-[47em] flex flex-col gap-[1.6em]">
        {/* Option 1 — QR (easiest) */}
        <div className="px-box px-box-dark p-[1.3em] flex items-center gap-[1.3em]">
          <div className="flex flex-col gap-[0.7em] flex-1">
            <div className="flex items-center gap-[0.55em]">
              <span className="font-num text-[2em] leading-none bg-[#FFD700] text-black px-[0.5em] py-[0.12em] rounded-[0.12em]">1</span>
              <span className="font-silk text-[1.7em] text-[#FFD700]">{tr(lang, "opt1_title")}</span>
            </div>
            <div className="font-pixel text-[1.6em] text-white leading-snug">{tr(lang, "opt1_desc")}</div>
          </div>
          <div className="px-box p-[0.8em] !bg-white shrink-0" data-testid="host-qr-code">
            <QRCodeSVG value={joinUrl} style={{ width: "13em", height: "13em", display: "block" }} />
          </div>
        </div>

        {/* Option 2 — type the link + PIN */}
        <div className="px-box px-box-dark p-[1.3em] flex flex-col gap-[0.7em]">
          <div className="flex items-center gap-[0.55em]">
            <span className="font-num text-[2em] leading-none bg-white text-black px-[0.5em] py-[0.12em] rounded-[0.12em]">2</span>
            <span className="font-silk text-[1.7em] text-[#FFD700]">{tr(lang, "opt2_title")}</span>
          </div>
          <div className="font-pixel text-[1.5em] text-slate-200 leading-snug">{tr(lang, "opt2_desc")}</div>
          <div className="font-pixel text-[2.5em] leading-tight text-white" data-testid="host-join-url">
            {window.location.host.split(".").map((part, i, arr) => (
              <span key={i}>{part}{i < arr.length - 1 && <>.<wbr /></>}</span>
            ))}
            <span className="text-[#FFD700]">/join</span>
          </div>
          <div className="font-pixel text-[1.4em] text-slate-300 leading-snug mt-[0.3em]">{tr(lang, "opt2_pin")}</div>
          <div className="flex items-center gap-[0.7em]">
            <span className="font-silk text-[1.5em] text-white">{tr(lang, "game_pin")}</span>
            <span data-testid="host-game-pin-display" className="font-num text-[3.6em] tracking-[0.14em] leading-none text-[#FFD700]">{game.pin}</span>
          </div>
        </div>
      </div>

      <div className="absolute left-[57em] right-[3em] top-[3em] bottom-[3em] flex flex-col">
        <div className="px-box px-box-dark px-[1.1em] py-[0.85em]">
          <div className="font-press text-[2em] text-[#FFD700] leading-snug" data-testid="host-quiz-title" style={{ textShadow: "0.1em 0.1em 0 #000" }}>{game.quiz.title}</div>
          <div className="flex items-center gap-[0.6em] mt-[0.7em] font-silk text-[1.8em] text-white">
            <Users style={{ width: "1.1em", height: "1.1em" }} />
            <span data-testid="host-player-count">{players.length}</span> {tr(lang, "players_joined")}
          </div>
        </div>
        <div className="flex-1 mt-[1.4em] flex flex-wrap content-start gap-[1.1em] overflow-hidden" data-testid="host-player-list">
          <AnimatePresence>
            {players.map((p) => (
              <motion.button
                key={p.id}
                layout
                initial={{ scale: 0.2, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0, opacity: 0 }}
                transition={{ type: "spring", stiffness: 400, damping: 18 }}
                onClick={() => onKick(p)}
                title={tr(lang, "kick_hint")}
                data-testid={`host-player-chip-${p.id}`}
                className="px-box font-pixel font-bold text-[2.1em] px-[0.6em] py-[0.15em] hover:line-through hover:!bg-rose-200"
              >
                {p.name}
              </motion.button>
            ))}
          </AnimatePresence>
          {players.length === 0 && (
            <div className="px-box px-box-dark px-[1em] py-[0.5em] font-pixel text-[1.8em] text-white blink">{tr(lang, "waiting_players")}</div>
          )}
        </div>
        <div className="flex items-end justify-between mt-[1em] gap-[1.5em]">
          <div className="px-box px-box-dark px-[0.9em] py-[0.45em] font-silk text-[1.5em] text-white max-w-[26em]">
            {players.length === 0 ? tr(lang, "need_players") : tr(lang, "kick_hint")}
          </div>
          <button data-testid="host-game-start-button" className="px-btn text-[2em] shrink-0" disabled={players.length === 0} onClick={onStart}>
            {tr(lang, "start_game")} ▶
          </button>
        </div>
      </div>
    </>
  );
};
